import { Children, ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import flatpickr from "flatpickr";

import Button from "../../ui/button/Button";
import DatePicker from "../../ui/form/date-picker";
import Label from "../../ui/form/Label";

/** The label style of every filter in the panel, dates or otherwise. */
export const FILTER_LABEL = "text-xs font-bold uppercase tracking-wider text-content-tertiary";

interface Props {
    /** Prefix for the pickers' ids, so two panels on a page do not collide. */
    idPrefix: string;
    from: string;
    to: string;
    onFromChange: (day: string) => void;
    onToChange: (day: string) => void;
    /** Put every filter back as it started. The panel redraws its pickers. */
    onClear: () => void;
    /** Further filters, drawn after the dates in the same panel. */
    children?: ReactNode;
    /**
     * A control for a second line, beside the clear button — for something
     * that shapes the result rather than narrowing it, like how to group it.
     */
    secondRow?: ReactNode;
}

// Static class names, so Tailwind sees them: two dates plus the extra filters.
const COLUMNS = ["md:grid-cols-2", "md:grid-cols-3", "md:grid-cols-2 lg:grid-cols-4"];

/**
 * A from–to date filter, the same one the agenda and every report use — and
 * the same panel as a patient's session list — so a date range reads the same
 * wherever somebody picks one.
 */
export default function DateRangeFilter({
    idPrefix,
    from,
    to,
    onFromChange,
    onToChange,
    onClear,
    children,
    secondRow,
}: Props) {
    const { t } = useTranslation();
    // flatpickr owns its input's value, so clearing remounts the pickers.
    const [resetKey, setResetKey] = useState(0);

    // Stable, because the date picker rebuilds its calendar whenever the
    // handler it was given changes. An emptied field keeps the last range
    // rather than asking the server for everything since the beginning.
    const handleFrom = useCallback(
        (dates: Date[]) => {
            if (dates[0]) onFromChange(flatpickr.formatDate(dates[0], "Y-m-d"));
        },
        [onFromChange]
    );
    const handleTo = useCallback(
        (dates: Date[]) => {
            if (dates[0]) onToChange(flatpickr.formatDate(dates[0], "Y-m-d"));
        },
        [onToChange]
    );

    const clear = () => {
        onClear();
        setResetKey((key) => key + 1);
    };

    const panel =
        "mb-5 flex flex-col gap-4 rounded-2xl border border-gray-100 bg-gray-50/50 p-4 dark:border-white/[0.05] dark:bg-white/[0.02]";

    const fields = (
        <div
            className={`grid w-full grid-cols-1 gap-4 ${COLUMNS[Math.min(Children.count(children), 2)]}`}
        >
            <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}-from`} className={FILTER_LABEL}>
                    {t("center.agenda.from")}
                </Label>
                <DatePicker
                    key={`from-${resetKey}`}
                    id={`${idPrefix}-from`}
                    placeholder={t("session.selectStartDate")}
                    defaultDate={from}
                    onChange={handleFrom}
                />
            </div>
            <div className="space-y-1.5">
                <Label htmlFor={`${idPrefix}-to`} className={FILTER_LABEL}>
                    {t("center.agenda.to")}
                </Label>
                <DatePicker
                    key={`to-${resetKey}`}
                    id={`${idPrefix}-to`}
                    placeholder={t("session.selectEndDate")}
                    defaultDate={to}
                    onChange={handleTo}
                />
            </div>
            {children}
        </div>
    );

    const clearButton = (
        <Button variant="outline" size="sm" onClick={clear} className="h-[42px] w-full sm:w-auto">
            {t("common.clear")}
        </Button>
    );

    if (!secondRow) {
        return (
            <div className={`${panel} items-end sm:flex-row`}>
                {fields}
                {clearButton}
            </div>
        );
    }

    return (
        <div className={panel}>
            {fields}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                {/* As wide as one column of the line above, so the two align. */}
                <div className="w-full md:w-[calc((100%-2rem)/3)]">{secondRow}</div>
                {clearButton}
            </div>
        </div>
    );
}
